import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../lib/api';
import { MagnifyingGlassIcon, CheckCircleIcon, XCircleIcon } from '@heroicons/react/24/outline';

interface ChefApplication {
  _id: string;
  user: {
    _id: string;
    name: string;
    email: string;
    avatar?: string;
  };
  chefBio: string;
  yearsOfExperience: number;
  specialties: string[];
  sampleRecipes?: Array<{
    _id: string;
    title: string;
    titleAr?: string;
  }>;
  socialMedia?: {
    instagram?: string;
    youtube?: string;
    tiktok?: string;
  };
  reasonForApplying: string;
  status: 'pending' | 'approved' | 'rejected';
  submittedAt: string;
  reviewedBy?: {
    _id: string;
    name: string;
  };
  reviewedAt?: string;
  rejectionReason?: string;
}

export default function ChefApplicationsTable() {
  const { t, i18n } = useTranslation();
  const isRtl = i18n.language === 'ar';
  const [applications, setApplications] = useState<ChefApplication[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [activeSearch, setActiveSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'pending' | 'approved' | 'rejected'>('all');
  
  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [limit, _setLimit] = useState(20);

  // Modal state
  const [selectedApplication, setSelectedApplication] = useState<ChefApplication | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [rejectionReason, setRejectionReason] = useState('');
  const [processing, setProcessing] = useState(false);
  
  // Confirmation modal state
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [confirmAction, setConfirmAction] = useState<'approve' | 'reject' | null>(null);
  const [confirmApplicationId, setConfirmApplicationId] = useState<string | null>(null);

  useEffect(() => {
    setCurrentPage(1);
  }, [activeSearch, statusFilter]);

  useEffect(() => {
    loadApplications();
  }, [currentPage, limit, activeSearch, statusFilter]);

  const loadApplications = async () => {
    try {
      setLoading(true);
      const params: any = { 
        page: currentPage,
        limit: limit,
      };
      
      if (activeSearch) params.search = activeSearch;
      if (statusFilter !== 'all') params.status = statusFilter;
      
      const response = await api.get('/chef/verification/requests', { params });
      setApplications(response.data.requests || []);
      setTotalPages(response.data.pagination?.totalPages || 1);
      setTotal(response.data.pagination?.total || 0);
    } catch (error) {
      console.error('Error loading chef applications:', error);
      alert(t('chefs.failedToLoad'));
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    setActiveSearch(search);
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const clearFilters = () => {
    setSearch('');
    setActiveSearch('');
    setStatusFilter('all');
    setCurrentPage(1);
  };

  const openConfirmModal = (action: 'approve' | 'reject', applicationId: string) => {
    // For reject action, check if rejection reason is provided
    if (action === 'reject' && !rejectionReason.trim()) {
      const errorMsg = document.createElement('div');
      errorMsg.className = 'fixed top-4 right-4 bg-red-500 text-white px-6 py-3 rounded-lg shadow-lg z-50';
      errorMsg.textContent = 'Please provide a rejection reason before confirming';
      document.body.appendChild(errorMsg);
      setTimeout(() => {
        document.body.removeChild(errorMsg);
      }, 3000);
      return;
    }
    
    setConfirmAction(action);
    setConfirmApplicationId(applicationId);
    setShowConfirmModal(true);
  };

  const closeConfirmModal = () => {
    setShowConfirmModal(false);
    setConfirmAction(null);
    setConfirmApplicationId(null);
  };

  const handleApprove = async () => {
    if (!confirmApplicationId) return;

    try {
      setProcessing(true);
      await api.post(`/chef/verification/requests/${confirmApplicationId}/approve`);
      loadApplications();
      setShowModal(false);
      setSelectedApplication(null);
      closeConfirmModal();
      
      // Show success message (you can replace with a toast notification if available)
      const successMsg = document.createElement('div');
      successMsg.className = 'fixed top-4 right-4 bg-green-500 text-white px-6 py-3 rounded-lg shadow-lg z-50';
      successMsg.textContent = 'Application approved successfully!';
      document.body.appendChild(successMsg);
      setTimeout(() => {
        document.body.removeChild(successMsg);
      }, 3000);
    } catch (error: any) {
      console.error('Error approving application:', error);
      const errorMsg = document.createElement('div');
      errorMsg.className = 'fixed top-4 right-4 bg-red-500 text-white px-6 py-3 rounded-lg shadow-lg z-50';
      errorMsg.textContent = error.response?.data?.message || 'Failed to approve application';
      document.body.appendChild(errorMsg);
      setTimeout(() => {
        document.body.removeChild(errorMsg);
      }, 3000);
    } finally {
      setProcessing(false);
    }
  };

  const handleReject = async () => {
    if (!confirmApplicationId) return;

    if (!rejectionReason.trim()) {
      const errorMsg = document.createElement('div');
      errorMsg.className = 'fixed top-4 right-4 bg-red-500 text-white px-6 py-3 rounded-lg shadow-lg z-50';
      errorMsg.textContent = 'Please provide a rejection reason';
      document.body.appendChild(errorMsg);
      setTimeout(() => {
        document.body.removeChild(errorMsg);
      }, 3000);
      return;
    }

    try {
      setProcessing(true);
      await api.post(`/chef/verification/requests/${confirmApplicationId}/reject`, {
        rejectionReason: rejectionReason.trim(),
      });
      loadApplications();
      setShowModal(false);
      setSelectedApplication(null);
      setRejectionReason('');
      closeConfirmModal();
      
      // Show success message
      const successMsg = document.createElement('div');
      successMsg.className = 'fixed top-4 right-4 bg-green-500 text-white px-6 py-3 rounded-lg shadow-lg z-50';
      successMsg.textContent = 'Application rejected successfully!';
      document.body.appendChild(successMsg);
      setTimeout(() => {
        document.body.removeChild(successMsg);
      }, 3000);
    } catch (error: any) {
      console.error('Error rejecting application:', error);
      const errorMsg = document.createElement('div');
      errorMsg.className = 'fixed top-4 right-4 bg-red-500 text-white px-6 py-3 rounded-lg shadow-lg z-50';
      errorMsg.textContent = error.response?.data?.message || t('chefs.failedToReject');
      document.body.appendChild(errorMsg);
      setTimeout(() => {
        document.body.removeChild(errorMsg);
      }, 3000);
    } finally {
      setProcessing(false);
    }
  };

  const openModal = (application: ChefApplication) => {
    setSelectedApplication(application);
    setRejectionReason(application.rejectionReason || '');
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setSelectedApplication(null);
    setRejectionReason('');
  };

  const getStatusBadge = (status: string) => {
    const styles = {
      pending: 'bg-yellow-100 text-yellow-800',
      approved: 'bg-green-100 text-green-800',
      rejected: 'bg-red-100 text-red-800',
    };
    
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-semibold ${styles[status as keyof typeof styles] || styles.pending}`}>
        {status === 'pending' ? t('chefs.pending') : status === 'approved' ? t('chefs.approved') : t('chefs.rejected')}
      </span>
    );
  };

  const getExperienceLabel = (years: number) => {
    if (years === 0) return t('chefs.experienceYears0');
    if (years === 2) return t('chefs.experienceYears2');
    if (years === 5) return t('chefs.experienceYears5');
    if (years === 10) return t('chefs.experienceYears10');
    return t('chefs.experienceYears', { count: years });
  };

  if (loading) {
    return (
      <div className="text-center py-12">
        <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary-orange"></div>
        <p className="mt-4 text-gray-600">{t('chefs.loading')}</p>
      </div>
    );
  }

  return (
    <div className={`space-y-4 ${isRtl ? 'text-right' : ''}`} dir={isRtl ? 'rtl' : 'ltr'}>
      <div className="bg-white rounded-lg shadow-md p-4">
        <div className={`flex flex-col md:flex-row gap-4 ${isRtl ? 'md:flex-row-reverse' : ''}`}>
          <div className="flex-1 relative flex items-center">
            <MagnifyingGlassIcon className={`absolute w-5 h-5 text-gray-400 ${isRtl ? 'right-3 left-auto' : 'left-3'}`} />
            <input
              type="text"
              placeholder={t('chefs.searchPlaceholder')}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyPress={handleKeyPress}
              className={`w-full py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-orange focus:border-transparent ${isRtl ? 'pr-10 pl-4' : 'pl-10 pr-4'}`}
            />
            <button
              onClick={handleSearch}
              className={`px-4 py-2 bg-primary-orange text-white rounded-lg hover:bg-orange-600 transition-colors ${isRtl ? 'mr-2 ml-0' : 'ml-2'}`}
            >
              {t('common.search')}
            </button>
          </div>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as any)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-orange focus:border-transparent"
          >
            <option value="all">{t('chefs.allStatus')}</option>
            <option value="pending">{t('chefs.pending')}</option>
            <option value="approved">{t('chefs.approved')}</option>
            <option value="rejected">{t('chefs.rejected')}</option>
          </select>
          {(activeSearch || statusFilter !== 'all') && (
            <button
              onClick={clearFilters}
              className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              {t('common.clearFilters')}
            </button>
          )}
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-md overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className={`px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider ${isRtl ? 'text-right' : 'text-left'}`}>{t('chefs.user')}</th>
                <th className={`px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider ${isRtl ? 'text-right' : 'text-left'}`}>{t('chefs.experience')}</th>
                <th className={`px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider ${isRtl ? 'text-right' : 'text-left'}`}>{t('chefs.specialties')}</th>
                <th className={`px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider ${isRtl ? 'text-right' : 'text-left'}`}>{t('chefs.date')}</th>
                <th className={`px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider ${isRtl ? 'text-right' : 'text-left'}`}>{t('chefs.status')}</th>
                <th className={`px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider ${isRtl ? 'text-right' : 'text-left'}`}>{t('chefs.actions')}</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {applications.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-6 py-12 text-center text-gray-500">
                    {t('chefs.noApplications')}
                  </td>
                </tr>
              ) : (
                applications.map((app) => (
                  <tr key={app._id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 h-10 w-10">
                          {app.user.avatar ? (
                            <img
                              className="h-10 w-10 rounded-full"
                              src={app.user.avatar}
                              alt={app.user.name}
                            />
                          ) : (
                            <div className="h-10 w-10 rounded-full bg-primary-orange flex items-center justify-center text-white font-semibold">
                              {app.user.name.charAt(0).toUpperCase()}
                            </div>
                          )}
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-900">{app.user.name}</div>
                          <div className="text-sm text-gray-500">{app.user.email}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{getExperienceLabel(app.yearsOfExperience)}</div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex flex-wrap gap-1">
                        {app.specialties.slice(0, 3).map((specialty, idx) => (
                          <span
                            key={idx}
                            className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-blue-100 text-blue-800"
                          >
                            {specialty}
                          </span>
                        ))}
                        {app.specialties.length > 3 && (
                          <span className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-gray-100 text-gray-800">
                            +{app.specialties.length - 3}
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {new Date(app.submittedAt).toLocaleDateString()}
                      </div>
                      <div className="text-sm text-gray-500">
                        {new Date(app.submittedAt).toLocaleTimeString()}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {getStatusBadge(app.status)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <div className={`flex flex-wrap items-center gap-3 ${isRtl ? 'flex-row-reverse justify-start' : 'justify-end'}`}>
                      <button
                        onClick={() => openModal(app)}
                        className="text-primary-orange hover:text-orange-600"
                      >
                        {t('chefs.viewDetails')}
                      </button>
                      {app.status === 'pending' && (
                        <>
                          <button
                            onClick={() => openConfirmModal('approve', app._id)}
                            className="text-green-600 hover:text-green-700"
                            disabled={processing}
                          >
                            {t('chefs.approve')}
                          </button>
                          <button
                            onClick={() => openModal(app)}
                            className="text-red-600 hover:text-red-700"
                            disabled={processing}
                          >
                            {t('chefs.reject')}
                          </button>
                        </>
                      )}
                    </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
            <div className="flex-1 flex justify-between sm:hidden">
              <button
                onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                disabled={currentPage === 1}
                className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
              >
                Previous
              </button>
              <button
                onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                disabled={currentPage === totalPages}
                className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
              >
                Next
              </button>
            </div>
            <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
              <div>
                <p className="text-sm text-gray-700">
                  Showing <span className="font-medium">{(currentPage - 1) * limit + 1}</span> to{' '}
                  <span className="font-medium">{Math.min(currentPage * limit, total)}</span> of{' '}
                  <span className="font-medium">{total}</span> results
                </p>
              </div>
              <div>
                <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                  <button
                    onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                    disabled={currentPage === 1}
                    className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                  >
                    Previous
                  </button>
                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    let pageNum;
                    if (totalPages <= 5) {
                      pageNum = i + 1;
                    } else if (currentPage <= 3) {
                      pageNum = i + 1;
                    } else if (currentPage >= totalPages - 2) {
                      pageNum = totalPages - 4 + i;
                    } else {
                      pageNum = currentPage - 2 + i;
                    }
                    return (
                      <button
                        key={pageNum}
                        onClick={() => setCurrentPage(pageNum)}
                        className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                          currentPage === pageNum
                            ? 'z-10 bg-primary-orange border-primary-orange text-white'
                            : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                        }`}
                      >
                        {pageNum}
                      </button>
                    );
                  })}
                  <button
                    onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                    disabled={currentPage === totalPages}
                    className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                  >
                    Next
                  </button>
                </nav>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Application Details Modal */}
      {showModal && selectedApplication && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 max-w-4xl shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900">Chef Application Details</h3>
                <button
                  onClick={closeModal}
                  className="text-gray-400 hover:text-gray-500"
                >
                  <XCircleIcon className="w-6 h-6" />
                </button>
              </div>

              <div className="space-y-6">
                {/* User Info */}
                <div>
                  <h4 className="text-sm font-semibold text-gray-700 mb-2">Applicant</h4>
                  <div className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg">
                    {selectedApplication.user.avatar ? (
                      <img
                        className="h-12 w-12 rounded-full"
                        src={selectedApplication.user.avatar}
                        alt={selectedApplication.user.name}
                      />
                    ) : (
                      <div className="h-12 w-12 rounded-full bg-primary-orange flex items-center justify-center text-white font-semibold">
                        {selectedApplication.user.name.charAt(0).toUpperCase()}
                      </div>
                    )}
                    <div>
                      <div className="text-sm font-medium text-gray-900">{selectedApplication.user.name}</div>
                      <div className="text-sm text-gray-500">{selectedApplication.user.email}</div>
                    </div>
                  </div>
                </div>

                {/* Bio */}
                <div>
                  <h4 className="text-sm font-semibold text-gray-700 mb-2">{t('chefs.bio')}</h4>
                  <p className="text-sm text-gray-900 p-3 bg-gray-50 rounded-lg">
                    {selectedApplication.chefBio}
                  </p>
                </div>

                {/* Experience & Specialties */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <h4 className="text-sm font-semibold text-gray-700 mb-2">{t('chefs.experience')}</h4>
                    <p className="text-sm text-gray-900">{getExperienceLabel(selectedApplication.yearsOfExperience)}</p>
                  </div>
                  <div>
                    <h4 className="text-sm font-semibold text-gray-700 mb-2">Specialties</h4>
                    <div className="flex flex-wrap gap-1">
                      {selectedApplication.specialties.map((specialty, idx) => (
                        <span
                          key={idx}
                          className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-blue-100 text-blue-800"
                        >
                          {specialty}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Sample Recipes */}
                {selectedApplication.sampleRecipes && selectedApplication.sampleRecipes.length > 0 && (
                  <div>
                    <h4 className="text-sm font-semibold text-gray-700 mb-2">Sample Recipes</h4>
                    <div className="space-y-2">
                      {selectedApplication.sampleRecipes.map((recipe) => (
                        <div key={recipe._id} className="p-2 bg-gray-50 rounded-lg">
                          <a
                            href={`/recipe/${recipe._id}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-sm text-primary-orange hover:underline"
                          >
                            {recipe.title}
                          </a>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Social Links */}
                {selectedApplication.socialMedia && (
                  Object.keys(selectedApplication.socialMedia).length > 0 && (
                    <div>
                      <h4 className="text-sm font-semibold text-gray-700 mb-2">Social Media</h4>
                      <div className="space-y-2">
                        {selectedApplication.socialMedia.instagram && (
                          <div className="flex items-center space-x-2">
                            <span className="text-sm font-medium text-gray-700">Instagram:</span>
                            <a
                              href={selectedApplication.socialMedia.instagram}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-sm text-primary-orange hover:underline"
                            >
                              {selectedApplication.socialMedia.instagram}
                            </a>
                          </div>
                        )}
                        {selectedApplication.socialMedia.youtube && (
                          <div className="flex items-center space-x-2">
                            <span className="text-sm font-medium text-gray-700">YouTube:</span>
                            <a
                              href={selectedApplication.socialMedia.youtube}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-sm text-primary-orange hover:underline"
                            >
                              {selectedApplication.socialMedia.youtube}
                            </a>
                          </div>
                        )}
                        {selectedApplication.socialMedia.tiktok && (
                          <div className="flex items-center space-x-2">
                            <span className="text-sm font-medium text-gray-700">TikTok:</span>
                            <a
                              href={selectedApplication.socialMedia.tiktok}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-sm text-primary-orange hover:underline"
                            >
                              {selectedApplication.socialMedia.tiktok}
                            </a>
                          </div>
                        )}
                      </div>
                    </div>
                  )
                )}

                {/* Reason for Applying */}
                <div>
                  <h4 className="text-sm font-semibold text-gray-700 mb-2">{t('chefs.reason')}</h4>
                  <p className="text-sm text-gray-900 p-3 bg-gray-50 rounded-lg">
                    {selectedApplication.reasonForApplying}
                  </p>
                </div>

                {/* Rejection Reason (if rejected) */}
                {selectedApplication.status === 'rejected' && selectedApplication.rejectionReason && (
                  <div>
                    <h4 className="text-sm font-semibold text-gray-700 mb-2">{t('chefs.rejectionReason')}</h4>
                    <p className="text-sm text-red-600 p-3 bg-red-50 rounded-lg">
                      {selectedApplication.rejectionReason}
                    </p>
                  </div>
                )}

                {/* Actions */}
                {selectedApplication.status === 'pending' && (
                  <div className="flex flex-wrap items-center justify-end gap-4 pt-4 border-t">
                    <div className="flex-1">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        {t('chefs.rejectionReasonOptional')}
                      </label>
                      <textarea
                        value={rejectionReason}
                        onChange={(e) => setRejectionReason(e.target.value)}
                        placeholder="Enter rejection reason..."
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-orange focus:border-transparent"
                        rows={3}
                      />
                    </div>
                    <div className="flex space-x-3">
                      <button
                        onClick={() => openConfirmModal('approve', selectedApplication._id)}
                        disabled={processing}
                        className="flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 transition-colors"
                      >
                        <CheckCircleIcon className="w-5 h-5 mr-2" />
                        {t('chefs.approve')}
                      </button>
                      <button
                        onClick={() => openConfirmModal('reject', selectedApplication._id)}
                        disabled={processing}
                        className="flex items-center px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 transition-colors"
                      >
                        <XCircleIcon className="w-5 h-5 mr-2" />
                        {t('chefs.reject')}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Confirmation Modal */}
      {showConfirmModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 flex items-center justify-center">
          <div className="relative bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="p-6">
              <div className="flex items-center justify-center w-12 h-12 mx-auto mb-4 rounded-full bg-red-100">
                {confirmAction === 'approve' ? (
                  <CheckCircleIcon className="w-6 h-6 text-green-600" />
                ) : (
                  <XCircleIcon className="w-6 h-6 text-red-600" />
                )}
              </div>
              <h3 className="text-lg font-medium text-gray-900 text-center mb-2">
                {confirmAction === 'approve' ? t('chefs.approveApplication') : t('chefs.rejectApplication')}
              </h3>
              <p className="text-sm text-gray-500 text-center mb-6">
                {confirmAction === 'approve'
                  ? t('chefs.confirmApproveMessage')
                  : t('chefs.confirmRejectMessage')}
              </p>
              <div className="flex space-x-3">
                <button
                  onClick={closeConfirmModal}
                  disabled={processing}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 disabled:opacity-50 transition-colors"
                >
                  {t('common.cancel')}
                </button>
                <button
                  onClick={confirmAction === 'approve' ? handleApprove : handleReject}
                  disabled={processing}
                  className={`flex-1 px-4 py-2 text-white rounded-lg hover:opacity-90 disabled:opacity-50 transition-colors ${
                    confirmAction === 'approve'
                      ? 'bg-green-600 hover:bg-green-700'
                      : 'bg-red-600 hover:bg-red-700'
                  }`}
                >
                  {processing ? (
                    <span className="flex items-center justify-center">
                      <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      {t('chefs.processing')}
                    </span>
                  ) : (
                    confirmAction === 'approve' ? t('chefs.approve') : t('chefs.reject')
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
